import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Typography,
} from 'antd';
import { CheckOutlined, DeleteOutlined, LinkOutlined, PlusOutlined, ShopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { FactoryOptionDto, SupplierOptionDto } from '../dto/reference';
import {
  createFactory,
  createSupplier,
  fetchFactoryOptions,
  fetchSupplierOptions,
} from '../modules/reference-data/api/referenceDataApi';
import {
  createSupplierOrder,
  fetchSupplierOrdersForCustomerOrder,
  fetchSuggestedSupplierOrderNumber,
  stripSupplierOrderItemPurchaseFields,
  stripSupplierOrderPurchaseFields,
  SupplierOrderPayload,
} from '../modules/orders/api/supplierOrdersApi';
import { fetchOrders } from '../modules/orders/api/ordersApi';
import { ORDER_CURRENCIES } from '../modules/orders/currencies';
import { formatSupplierOrderQuantity, SUPPLIER_ORDER_UNITS } from '../modules/orders/supplierOrderUnits';
import {
  buildCustomerOrderLinkOption,
  buildCustomerOrderLinkOptions,
} from '../modules/orders/customerOrderLinkOptions';
import { buildSupplierOrderLinkPayload } from '../modules/orders/supplierOrderLinkPayload';
import SupplierOrderPurchaseModeSelector from '../modules/orders/sections/SupplierOrderPurchaseModeSelector';
import {
  buildSupplierItemsFromCustomerOrder,
  calculateSupplierItemAllocationBalance,
  changeSupplierItemUnit,
  findInvalidSupplierItemAllocationIndex,
  SupplierOrderPurchaseMode,
  SupplierOrderSurplusDisposition,
} from '../modules/orders/supplierOrderCreateMode';
import { authService } from '../services/auth';
import { applySupplierSaveError } from '../components/suppliers/supplierFormErrors';
import ProductNameCombobox from '../components/inventory/ProductNameCombobox';
import { ProductVariant } from '../types/pz';
import { formatProductDimensions } from '../utils/productDimensions';
import {
  buildSupplierOrderVariantAutofill,
  isDefaultPurchaseAutofillApplied,
} from '../modules/orders/supplierOrderVariantUtils';
import {
  supplierOrderAmountRule,
  supplierOrderNumberRule,
  supplierOrderQuantityRule,
} from '../dto/supplierOrder';
import OrderDraftToolbar, { OrderDraftToolbarHandle } from '../components/orders/OrderDraftToolbar';
import { OrderDto } from '../dto/order';
import { formatUnit } from '../utils/units';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import './SupplierOrderCreateWizard.css';

const { Text, Title } = Typography;

const MAX_ITEMS = 200;
const SUPPLIER_CURRENCIES = ['USD', 'EUR', 'PLN', 'GBP', 'CNY'];

type FieldPath = string | (string | number)[];
type SupplierOrderItemDraft = {
  sourceOrderProductId?: string;
  productVariantId?: string | null;
  name?: string;
  quantity?: number | string;
  unit?: string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
  thickness?: number | string;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  currency?: string;
  unitCost?: number | string;
  defaultPurchasePrice?: number | string | null;
  defaultPurchaseCurrency?: string | null;
  customerQuantity?: number | string;
  customerUnit?: 'pcs' | 'm2' | 'm3';
  surplusDisposition?: SupplierOrderSurplusDisposition;
};

type SupplierOrderSummaryItemDraft = SupplierOrderItemDraft & {
  summaryKey: string;
};

// Returns the per-step field paths to validate on Dalej. Step 1's `items` is a
// Form.List — Ant Design's validateFields(['items']) does NOT recurse into
// child rules, so we enumerate each row's required nested fields.
const getStepFields = (
  step: number,
  items: any[] = [],
  requireUnitCost = true,
  useManualNumbering = false,
  purchaseMode: SupplierOrderPurchaseMode = 'CUSTOMER_ORDER',
): FieldPath[] => {
  if (step === 0) {
    const fields: FieldPath[] = ['purchaseMode', 'supplierId', 'orderDate'];
    if (purchaseMode === 'CUSTOMER_ORDER') fields.push('customerOrderId');
    if (useManualNumbering) fields.push('manualOrderNumber');
    return fields;
  }
  if (step === 1) {
    const paths: FieldPath[] = [];
    if (purchaseMode === 'CUSTOMER_ORDER') paths.push('items');
    items.forEach((_, idx) => {
      paths.push(['items', idx, 'name']);
      paths.push(['items', idx, 'quantity']);
      paths.push(['items', idx, 'unit']);
      if (purchaseMode === 'CUSTOMER_ORDER') {
        paths.push(['items', idx, 'customerQuantity']);
      }
      if (requireUnitCost) {
        paths.push(['items', idx, 'currency']);
        paths.push(['items', idx, 'unitCost']);
      }
    });
    return paths;
  }
  if (step === 2) return [];
  return [];
};

// Drafts persist server-side as JSON, so dayjs date fields round-trip as ISO.
function serializeSupplierOrderDraftValues(values: any): Record<string, any> {
  const serializable = { ...values };
  if (serializable.orderDate && dayjs.isDayjs(serializable.orderDate)) {
    serializable.orderDate = serializable.orderDate.toISOString();
  }
  if (serializable.expectedDelivery && dayjs.isDayjs(serializable.expectedDelivery)) {
    serializable.expectedDelivery = serializable.expectedDelivery.toISOString();
  }
  return serializable;
}

function deserializeSupplierOrderDraftPayload(payload: Record<string, any>): Record<string, any> {
  const parsed = { ...payload };
  if (parsed.orderDate) parsed.orderDate = dayjs(parsed.orderDate);
  if (parsed.expectedDelivery) parsed.expectedDelivery = dayjs(parsed.expectedDelivery);
  return parsed;
}

const formatDraftMoney = (value: number, currency: string): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

// Adapter so Form.Item can inject value/onChange for the `name` field while
// ProductNameCombobox's onSelectVariant/onDetachVariant callbacks still drive
// the row's catalog-linked fields (productVariantId, dims, price, etc.).
const SupplierProductNameField: React.FC<{
  value?: string;
  onChange?: (name: string) => void;
  onSelectVariant: (variantId: string | undefined, variant?: ProductVariant) => void;
  onDetachVariant: () => void;
  allowCreate?: boolean;
  placeholder?: string;
  preferredVariantIds?: readonly string[];
  selectedVariantId?: string | null;
}> = ({ value, onChange, onSelectVariant, onDetachVariant, allowCreate, placeholder, preferredVariantIds, selectedVariantId }) => (
  <ProductNameCombobox
    value={value}
    onChange={(text) => { onChange?.(text); onDetachVariant(); }}
    onSelectVariant={onSelectVariant}
    allowCreate={allowCreate}
    showInventory={false}
    placeholder={placeholder}
    preferredVariantIds={preferredVariantIds}
    selectedVariantId={selectedVariantId}
    purchasePriceScope="supplier-order"
  />
);

const SupplierOrderCreateWizard: React.FC = () => {
  const { t, i18n } = useTranslation(['orders', 'common', 'suppliers']);
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const [form] = Form.useForm();
  const [supplierForm] = Form.useForm();
  const [factoryForm] = Form.useForm();

  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [supplierSubmitting, setSupplierSubmitting] = useState(false);
  const [factorySubmitting, setFactorySubmitting] = useState(false);
  const [customerItemsLoading, setCustomerItemsLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierOptionDto[]>([]);
  const [factories, setFactories] = useState<FactoryOptionDto[]>([]);
  const [customerOrders, setCustomerOrders] = useState<OrderDto[]>([]);
  const [supplierModalVisible, setSupplierModalVisible] = useState(false);
  const [factoryModalVisible, setFactoryModalVisible] = useState(false);
  const [autoNumberPreview, setAutoNumberPreview] = useState<string | null>(null);
  const watchedOrderDate = Form.useWatch('orderDate', form);
  const initializedRef = useRef(false);
  const draftToolbarRef = useRef<OrderDraftToolbarHandle>(null);
  const canEditSupplierPurchasePrices = authService.canEditSupplierOrderPurchasePrices();
  const watchedPurchaseMode = Form.useWatch('purchaseMode', form) as SupplierOrderPurchaseMode | undefined;
  const customerOrderOptions = useMemo(
    () => buildCustomerOrderLinkOptions(customerOrders),
    [customerOrders]
  );

  const buildDraftPayload = () => serializeSupplierOrderDraftValues(form.getFieldsValue(true));

  const applyDraftPayload = (payload: Record<string, any>) => {
    const draft = deserializeSupplierOrderDraftPayload(payload);
    if (!draft.purchaseMode) {
      draft.purchaseMode = draft.customerOrderId ? 'CUSTOMER_ORDER' : 'STOCK';
    }
    // Mirror the create payload guard: a user who cannot see purchase prices must
    // not have currency/unitCost injected back from a draft they shouldn't edit.
    if (!canEditSupplierPurchasePrices) {
      delete draft.currency;
      if (Array.isArray(draft.items)) {
        draft.items = draft.items.map(stripSupplierOrderItemPurchaseFields);
      }
    }
    form.setFieldsValue(draft);
  };

  const draftDefaultName = () => {
    const supplier = suppliers.find((item) => item.id === form.getFieldValue('supplierId'));
    const customerOrder = customerOrders.find((item) => item.id === form.getFieldValue('customerOrderId'));
    return [customerOrder ? buildCustomerOrderLinkOption(customerOrder).label : null, supplier?.name].filter(Boolean).join(' - ');
  };

  const fetchReferenceData = async () => {
    try {
      const [supplierRows, factoryRows, customerOrderRows] = await Promise.all([
        fetchSupplierOptions(),
        fetchFactoryOptions(),
        fetchOrders({ isQuote: false }, 'supplierOrderCreate.customerOrders'),
      ]);
      setSuppliers(supplierRows);
      setFactories(factoryRows);
      setCustomerOrders(customerOrderRows);
    } catch {
      // Non-critical — selects will be empty
    }
  };

  useEffect(() => {
    void fetchReferenceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Guard against StrictMode double-fire and any later dep changes — defaults
    // and draft restoration must apply exactly once per page mount.
    if (initializedRef.current) return;
    initializedRef.current = true;

    const defaults: Record<string, any> = {
      purchaseMode: 'CUSTOMER_ORDER',
      status: 'DRAFT',
      orderDate: dayjs(),
      items: [],
    };
    if (canEditSupplierPurchasePrices) {
      defaults.currency = 'PLN';
    }

    form.setFieldsValue(defaults);
  }, [form, canEditSupplierPurchasePrices]);

  // Surface the predicted automatic supplier-order number in the wizard, scoped
  // to the chosen business month (mirrors customer order numbering). Read-only
  // preview — the value is a prediction, the binding number is assigned
  // server-side at create time.
  useEffect(() => {
    const orderDateValue = form.getFieldValue('orderDate');
    const orderDateIso = dayjs.isDayjs(orderDateValue) ? orderDateValue.toISOString() : undefined;
    let ignore = false;
    (async () => {
      try {
        const suggestion = await fetchSuggestedSupplierOrderNumber(orderDateIso);
        if (!ignore) setAutoNumberPreview(suggestion);
      } catch {
        // Non-critical — preview only
      }
    })();
    return () => {
      ignore = true;
    };
  }, [watchedOrderDate, form]);

  const handleNext = async () => {
    const items = form.getFieldValue('items') || [];
    const fieldsToValidate = getStepFields(
      currentStep,
      items,
      canEditSupplierPurchasePrices,
      Boolean(form.getFieldValue('useManualNumbering')),
      form.getFieldValue('purchaseMode') || 'CUSTOMER_ORDER',
    );
    if (fieldsToValidate.length > 0) {
      try {
        await form.validateFields(fieldsToValidate);
      } catch {
        return;
      }
    }
    setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    setCurrentStep((s) => s - 1);
  };

  const buildBlankStockItem = (): SupplierOrderItemDraft => (
    canEditSupplierPurchasePrices
      ? { name: '', quantity: 1, unit: 'pcs', currency: 'PLN', unitCost: 0 }
      : { name: '', quantity: 1, unit: 'pcs' }
  );

  const handlePurchaseModeChange = (mode: SupplierOrderPurchaseMode) => {
    if (mode === 'STOCK') {
      setCustomerItemsLoading(false);
      form.setFieldValue('customerOrderId', null);
      form.setFieldValue('customerOrderLink', null);
      form.setFieldValue('items', [buildBlankStockItem()]);
      return;
    }

    const customerOrder = customerOrders.find((order) => order.id === form.getFieldValue('customerOrderId'));
    if (customerOrder) {
      void handleCustomerOrderChange(customerOrder.id);
    } else {
      form.setFieldValue('items', []);
    }
  };

  const handleCustomerOrderChange = async (orderId?: string) => {
    const customerOrder = customerOrders.find((order) => order.id === orderId);
    if (!customerOrder || !orderId) {
      setCustomerItemsLoading(false);
      form.setFieldValue('items', []);
      return;
    }

    setCustomerItemsLoading(true);
    let previousAllocations: Array<{
      orderProductId: string;
      customerQuantity: number;
      customerUnit: string;
    }> = [];
    try {
      const linkedSupplierOrders = await fetchSupplierOrdersForCustomerOrder(orderId);
      previousAllocations = linkedSupplierOrders.flatMap((supplierOrder) => (
        supplierOrder.itemAllocations || []
      )).map((allocation) => ({
        orderProductId: allocation.orderProductId,
        customerQuantity: allocation.customerQuantity,
        customerUnit: allocation.customerUnit,
      }));
    } catch {
      // The remaining-quantity hint is helpful but non-critical. Backend
      // validation remains authoritative if historical links cannot be read.
    }

    if (form.getFieldValue('customerOrderId') === orderId) {
      form.setFieldValue('items', buildSupplierItemsFromCustomerOrder(
        customerOrder,
        canEditSupplierPurchasePrices,
        previousAllocations,
      ));
      setCustomerItemsLoading(false);
    }
  };

  const handleSupplierItemUnitChange = (index: number, nextUnit: 'pcs' | 'm2' | 'm3') => {
    const item = form.getFieldValue(['items', index]) as SupplierOrderItemDraft | undefined;
    if (!item) return;

    const convertedQuantity = changeSupplierItemUnit(item as any, nextUnit);
    form.setFieldValue(['items', index, 'unit'], nextUnit);
    if (convertedQuantity !== null) {
      form.setFieldValue(['items', index, 'quantity'], convertedQuantity);
    }
  };

  const handleCancel = () => {
    if (form.isFieldsTouched()) {
      modal.confirm({
        title: t('orders:wizard.confirmCancelTitle'),
        content: t('orders:wizard.confirmCancelContent'),
        okText: t('common:yes'),
        cancelText: t('common:no'),
        onOk: () => {
          navigate('/app/orders');
        },
      });
    } else {
      navigate('/app/orders');
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      const {
        purchaseMode,
        customerOrderId,
        customerOrderLink: rawCustomerOrderLink,
        ...supplierOrderValues
      } = values;
      if (purchaseMode === 'CUSTOMER_ORDER') {
        const invalidAllocationIndex = findInvalidSupplierItemAllocationIndex(values.items || []);
        if (invalidAllocationIndex >= 0) {
          const validationMessage = t('orders:supplierOrders.allocation.missingDimensions');
          form.setFields([{
            name: ['items', invalidAllocationIndex, 'customerQuantity'],
            errors: [validationMessage],
          }]);
          message.error(validationMessage);
          return;
        }
      }
      // `items` and `itemAllocations[].supplierOrderItemIndex` below index the
      // same `values.items` array — that is the only link between a customer
      // line and a supplier item that does not exist yet. Never filter, sort or
      // reorder one of these maps without the other.
      const items = (values.items || []).map((item: any) => {
        const {
          sourceOrderProductId,
          customerQuantity,
          customerUnit,
          surplusDisposition,
          productCategoryName,
          productCategoryColor,
          productCategoryDimensionDisplayProfile,
          defaultPurchasePrice,
          defaultPurchaseCurrency,
          height,
          ...persistableItem
        } = item;
        const itemWithDefaultUnit = { ...persistableItem, unit: persistableItem.unit || 'pcs' };
        if (canEditSupplierPurchasePrices) {
          return itemWithDefaultUnit;
        }

        return stripSupplierOrderItemPurchaseFields(itemWithDefaultUnit);
      });
      const itemAllocations = purchaseMode === 'CUSTOMER_ORDER'
        ? (values.items || []).map((item: SupplierOrderItemDraft, index: number) => ({
            orderProductId: item.sourceOrderProductId || '',
            supplierOrderItemIndex: index,
            customerQuantity: Number(item.customerQuantity || 0),
            customerUnit: item.customerUnit || 'pcs',
            surplusDisposition: item.surplusDisposition || 'STOCK',
          }))
        : undefined;
      const customerOrderLink = purchaseMode === 'CUSTOMER_ORDER' && customerOrderId
        ? buildSupplierOrderLinkPayload({
            ...(rawCustomerOrderLink || {}),
            itemAllocations,
          }, {
            orderId: customerOrderId,
            canEditSupplierOrderFinance: false,
          })
        : null;
      const payload: SupplierOrderPayload = {
        ...supplierOrderValues,
        factoryId: values.factoryId || null,
        orderDate: values.orderDate?.toISOString(),
        expectedDelivery: values.expectedDelivery?.toISOString() || null,
        supplierReference: values.supplierReference || null,
        proformaNumber: values.proformaNumber || null,
        notes: values.notes || null,
        items,
        useManualNumbering: Boolean(values.useManualNumbering),
        manualOrderNumber: values.useManualNumbering ? values.manualOrderNumber?.trim() || null : null,
      };
      if (customerOrderLink) {
        payload.customerOrderLink = customerOrderLink;
      } else {
        delete payload.customerOrderLink;
      }
      const created = await createSupplierOrder(
        canEditSupplierPurchasePrices ? payload : stripSupplierOrderPurchaseFields(payload)
      );
      await draftToolbarRef.current?.discardLoadedDraft();
      message.success(t('orders:supplierOrders.messages.createSuccess'));
      navigate(created?.id ? `/app/supplier-orders/${created.id}` : '/app/orders?orderFlow=supplier');
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(
        error.response?.data?.message || t('orders:supplierOrders.messages.createError')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVariantChange = (index: number, variantId: string | undefined, variant?: ProductVariant) => {
    form.setFieldValue(['items', index, 'productVariantId'], variantId || null);

    if (!variant) {
      form.setFieldValue(['items', index, 'name'], '');
      form.setFieldValue(['items', index, 'length'], null);
      form.setFieldValue(['items', index, 'width'], null);
      form.setFieldValue(['items', index, 'height'], null);
      form.setFieldValue(['items', index, 'thickness'], null);
      form.setFieldValue(['items', index, 'productCategoryName'], null);
      form.setFieldValue(['items', index, 'productCategoryColor'], null);
      form.setFieldValue(['items', index, 'productCategoryDimensionDisplayProfile'], null);
      form.setFieldValue(['items', index, 'defaultPurchasePrice'], null);
      form.setFieldValue(['items', index, 'defaultPurchaseCurrency'], null);
      return;
    }

    const autofill = buildSupplierOrderVariantAutofill(variant);
    form.setFieldValue(['items', index, 'name'], autofill.name);
    form.setFieldValue(['items', index, 'unit'], autofill.unit);
    form.setFieldValue(['items', index, 'length'], autofill.length ?? null);
    form.setFieldValue(['items', index, 'width'], autofill.width ?? null);
    form.setFieldValue(['items', index, 'height'], autofill.height ?? null);
    form.setFieldValue(['items', index, 'thickness'], autofill.thickness ?? null);
    form.setFieldValue(['items', index, 'productCategoryName'], autofill.productCategoryName ?? null);
    form.setFieldValue(['items', index, 'productCategoryColor'], autofill.productCategoryColor ?? null);
    form.setFieldValue(
      ['items', index, 'productCategoryDimensionDisplayProfile'],
      autofill.productCategoryDimensionDisplayProfile ?? null
    );

    if (canEditSupplierPurchasePrices && autofill.defaultPurchasePrice != null) {
      form.setFieldValue(['items', index, 'unitCost'], autofill.defaultPurchasePrice);
      if (autofill.defaultPurchaseCurrency && ORDER_CURRENCIES.includes(autofill.defaultPurchaseCurrency as typeof ORDER_CURRENCIES[number])) {
        form.setFieldValue(['items', index, 'currency'], autofill.defaultPurchaseCurrency);
      }
    }
    form.setFieldValue(['items', index, 'defaultPurchasePrice'], autofill.defaultPurchasePrice ?? null);
    form.setFieldValue(['items', index, 'defaultPurchaseCurrency'], autofill.defaultPurchaseCurrency ?? null);
  };

  const handleCreateFactory = async (values: any) => {
    setFactorySubmitting(true);
    try {
      const factory = await createFactory({
        ...values,
        taxId: values.taxId || null,
        address: values.address || null,
      });
      message.success(t('orders:supplierOrders.messages.factoryCreateSuccess'));
      setFactories((current) =>
        [...current, factory].sort((a, b) => a.name.localeCompare(b.name))
      );
      form.setFieldValue('factoryId', factory.id);
      setFactoryModalVisible(false);
      factoryForm.resetFields();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || t('orders:supplierOrders.messages.factoryCreateError')
      );
    } finally {
      setFactorySubmitting(false);
    }
  };

  const handleOpenSupplierModal = () => {
    supplierForm.resetFields();
    supplierForm.setFieldsValue({ defaultCurrency: 'PLN' });
    setSupplierModalVisible(true);
  };

  const handleCreateSupplier = async (values: any) => {
    setSupplierSubmitting(true);
    supplierForm.setFields([{ name: 'taxId', errors: [] }]);
    try {
      const contactName = values.contactName?.trim();
      const contactPhone = values.contactPhone?.trim();
      const contactEmail = values.contactEmail?.trim();
      const contactPeople = contactName || contactPhone || contactEmail
        ? [{ name: contactName || null, phone: contactPhone || null, email: contactEmail || null }]
        : [];

      const supplier = await createSupplier({
        name: values.name,
        contactPeople,
        contactPerson: contactPeople[0]?.name || null,
        phone: contactPeople[0]?.phone || null,
        email: contactPeople[0]?.email || null,
        taxId: values.taxId || null,
        address: values.address || null,
        defaultCurrency: values.defaultCurrency || 'PLN',
      });

      message.success(t('orders:supplierOrders.messages.supplierCreateSuccess'));
      setSuppliers((current) =>
        [...current.filter((item) => item.id !== supplier.id), supplier]
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      form.setFieldValue('supplierId', supplier.id);
      setSupplierModalVisible(false);
      supplierForm.resetFields();
    } catch (error: any) {
      applySupplierSaveError(
        error,
        supplierForm,
        t,
        message,
        t('orders:supplierOrders.messages.supplierCreateError')
      );
    } finally {
      setSupplierSubmitting(false);
    }
  };

  const stepItems = [
    {
      title: t('orders:wizard.supplierSteps.basics'),
      description: t('orders:wizard.supplierStepDescriptions.basics'),
    },
    {
      title: t('orders:wizard.steps.items'),
      description: t('orders:wizard.supplierStepDescriptions.items'),
    },
    {
      title: t('orders:wizard.steps.datesRefs'),
      description: t('orders:wizard.supplierStepDescriptions.datesRefs'),
    },
    {
      title: t('orders:wizard.steps.summary'),
      description: t('orders:wizard.supplierStepDescriptions.summary'),
    },
  ];

  const renderStepRail = () => (
    <nav className="supplier-order-create-progress" aria-label={t('orders:wizard.progressLabel')}>
      {stepItems.map((item, index) => {
        const isActive = index === currentStep;
        const isComplete = index < currentStep;

        return (
          <div
            key={item.title}
            className={`supplier-order-create-progress__step${isActive ? ' is-active' : ''}${isComplete ? ' is-complete' : ''}`}
            aria-current={isActive ? 'step' : undefined}
          >
            <span className="supplier-order-create-progress__index" aria-hidden="true">
              {isComplete ? <CheckOutlined /> : index + 1}
            </span>
            <span className="supplier-order-create-progress__copy">
              <strong>{item.title}</strong>
              <span>{item.description}</span>
            </span>
          </div>
        );
      })}
    </nav>
  );

  const renderStep0 = () => (
    <section className="supplier-order-create-section supplier-order-create-basics">
      <div className="supplier-order-create-link-block">
        <div className="supplier-order-create-group-heading">
          <span className="supplier-order-create-group-icon" aria-hidden="true">
            <LinkOutlined />
          </span>
          <div>
            <Title level={4}>{t('orders:wizard.supplierBasics.customerOrderTitle')}</Title>
            <Text type="secondary">{t('orders:wizard.supplierBasics.customerOrderHint')}</Text>
          </div>
        </div>
        <div className="supplier-order-create-link-field">
          <Form.Item
            name="purchaseMode"
            label={t('orders:supplierOrders.purchaseMode.label')}
            rules={[{ required: true }]}
          >
            <SupplierOrderPurchaseModeSelector onChange={handlePurchaseModeChange} />
          </Form.Item>
          {watchedPurchaseMode === 'CUSTOMER_ORDER' && (
            <Form.Item
              name="customerOrderId"
              label={t('orders:supplierOrders.form.customerOrder')}
              rules={[{ required: true, message: t('orders:supplierOrders.form.customerOrderRequired') }]}
              extra={t('orders:supplierOrders.purchaseMode.customerOrderSelectHelp')}
              style={{ marginTop: 12 }}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={customerOrderOptions}
                placeholder={t('orders:supplierOrders.form.selectCustomerOrder')}
                onChange={handleCustomerOrderChange}
              />
            </Form.Item>
          )}
        </div>
      </div>

      <div className="supplier-order-create-content-group">
        <div className="supplier-order-create-group-heading">
          <div>
            <Title level={4}>{t('orders:wizard.supplierBasics.supplierTitle')}</Title>
            <Text type="secondary">{t('orders:wizard.supplierBasics.supplierHint')}</Text>
          </div>
        </div>

        <Row gutter={[24, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item label={t('orders:supplierOrders.form.supplier')} required>
              <Form.Item
                name="supplierId"
                noStyle
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  optionFilterProp="searchText"
                  options={suppliers.map((s) => ({ value: s.id, label: s.name, searchText: s.searchText }))}
                  popupRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <Button
                        type="text"
                        block
                        icon={<PlusOutlined />}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={handleOpenSupplierModal}
                      >
                        {t('orders:supplierOrders.form.addSupplier')}
                      </Button>
                    </>
                  )}
                />
              </Form.Item>
              <Button
                className="supplier-order-create-inline-action"
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleOpenSupplierModal}
              >
                {t('orders:supplierOrders.form.addSupplier')}
              </Button>
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item label={t('orders:supplierOrders.form.factory')}>
              <Form.Item name="factoryId" noStyle>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={factories.map((f) => ({ value: f.id, label: f.name }))}
                />
              </Form.Item>
              <Button
                className="supplier-order-create-inline-action"
                type="link"
                size="small"
                icon={<ShopOutlined />}
                onClick={() => setFactoryModalVisible(true)}
              >
                {t('orders:supplierOrders.form.addFactory')}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="supplier-order-create-content-group supplier-order-create-content-group--last">
        <div className="supplier-order-create-group-heading">
          <div>
            <Title level={4}>{t('orders:wizard.supplierBasics.detailsTitle')}</Title>
            <Text type="secondary">{t('orders:wizard.supplierBasics.detailsHint')}</Text>
          </div>
        </div>

        <Row gutter={[24, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="orderDate"
              label={t('orders:supplierOrders.form.orderDate')}
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <div className="supplier-order-create-numbering">
              <div className="supplier-order-create-numbering__topline">
                <div>
                  <Text strong>{t('orders:supplierOrders.form.useManualNumbering')}</Text>
                  <Text type="secondary">{t('orders:wizard.supplierBasics.manualNumberingHint')}</Text>
                </div>
                <Form.Item
                  name="useManualNumbering"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch aria-label={t('orders:supplierOrders.form.useManualNumbering')} />
                </Form.Item>
              </div>

              <Form.Item shouldUpdate={(prev, next) => prev.useManualNumbering !== next.useManualNumbering} noStyle>
                {({ getFieldValue }) => getFieldValue('useManualNumbering') ? (
                  <Form.Item
                    className="supplier-order-create-numbering__field"
                    name="manualOrderNumber"
                    label={t('orders:supplierOrders.form.manualOrderNumber')}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: t('orders:modal.validation.manualNumberRequired'),
                      },
                      supplierOrderNumberRule(t),
                    ]}
                  >
                    <Input
                      maxLength={64}
                      placeholder={t('orders:supplierOrders.form.manualOrderNumberPlaceholder')}
                    />
                  </Form.Item>
                ) : (
                  <div className="supplier-order-create-numbering__preview">
                    <Text type="secondary">{t('orders:supplierOrders.form.generatedNumberLabel')}</Text>
                    <strong>{autoNumberPreview || '—'}</strong>
                    <Text type="secondary">{t('orders:supplierOrders.form.automaticNumberingHint')}</Text>
                  </div>
                )}
              </Form.Item>
            </div>
          </Col>
        </Row>
      </div>
    </section>
  );

  const renderStep1 = () => (
    <Card title={t('orders:wizard.steps.items')}>
      <Form.Item name="currency" hidden>
        <Input />
      </Form.Item>

      <Form.List
        name="items"
        initialValue={[]}
        rules={[{
          validator: async (_, items) => {
            if (!Array.isArray(items) || items.length === 0) {
              throw new Error(t('orders:supplierOrders.allocation.noRemainingQuantity'));
            }
          },
        }]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {watchedPurchaseMode === 'CUSTOMER_ORDER' && fields.length === 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('orders:supplierOrders.allocation.noRemainingQuantity')}
              />
            )}
            {fields.map((field, index) => {
              const { key, ...fieldProps } = field;
              return (
                <Card
                  key={key}
                  size="small"
                  title={`${t('orders:modal.product')} ${index + 1}`}
                  style={{ marginBottom: 12 }}
                  extra={
                    watchedPurchaseMode === 'STOCK' && fields.length > 1 && (
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    )
                  }
                >
                  <Form.Item {...fieldProps} name={[field.name, 'productVariantId']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'sourceOrderProductId']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'customerUnit']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'length']} hidden>
                    <InputNumber />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'width']} hidden>
                    <InputNumber />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'height']} hidden>
                    <InputNumber />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'thickness']} hidden>
                    <InputNumber />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'productCategoryName']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'productCategoryColor']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'productCategoryDimensionDisplayProfile']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'defaultPurchasePrice']} hidden>
                    <InputNumber />
                  </Form.Item>
                  <Form.Item {...fieldProps} name={[field.name, 'defaultPurchaseCurrency']} hidden>
                    <Input />
                  </Form.Item>

                  <Row gutter={[16, 0]}>
                    <Col xs={24}>
                      <Form.Item
                        {...fieldProps}
                        name={[field.name, 'name']}
                        label={t('orders:modal.product')}
                        rules={[{ required: true }]}
                      >
                        {watchedPurchaseMode === 'CUSTOMER_ORDER' ? (
                          <Input readOnly variant="filled" />
                        ) : (
                          <SupplierProductNameField
                            allowCreate
                            onSelectVariant={(variantId, variant) => handleVariantChange(index, variantId, variant)}
                            onDetachVariant={() => {
                              form.setFieldValue(['items', field.name, 'productVariantId'], null);
                              form.setFieldValue(['items', field.name, 'length'], null);
                              form.setFieldValue(['items', field.name, 'width'], null);
                              form.setFieldValue(['items', field.name, 'height'], null);
                              form.setFieldValue(['items', field.name, 'thickness'], null);
                              form.setFieldValue(['items', field.name, 'productCategoryName'], null);
                              form.setFieldValue(['items', field.name, 'productCategoryColor'], null);
                              form.setFieldValue(['items', field.name, 'productCategoryDimensionDisplayProfile'], null);
                              form.setFieldValue(['items', field.name, 'defaultPurchasePrice'], null);
                              form.setFieldValue(['items', field.name, 'defaultPurchaseCurrency'], null);
                            }}
                            placeholder={t('orders:supplierOrders.form.selectProductVariant')}
                          />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 0]}>
                    <Col xs={12} md={4}>
                      <Form.Item
                        {...fieldProps}
                        name={[field.name, 'quantity']}
                        label={t('orders:quantity')}
                        rules={[{ required: true }, supplierOrderQuantityRule(t)]}
                      >
                        <InputNumber
                          step={0.001}
                          precision={3}
                          style={{ width: '100%' }}
                          {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Item
                        {...fieldProps}
                        name={[field.name, 'unit']}
                        label={t('orders:supplierOrders.form.allocatedUnit')}
                        rules={[{ required: true }]}
                      >
                        <Select
                          options={SUPPLIER_ORDER_UNITS.map((unit) => ({ value: unit, label: formatUnit(unit) }))}
                          onChange={(unit) => handleSupplierItemUnitChange(index, unit)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Item label={t('orders:modal.dimensionsShort')}>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => {
                            const dims = formatProductDimensions(getFieldValue(['items', field.name]) || {}) || '—';
                            return (
                              <Input
                                readOnly
                                variant="filled"
                                value={dims}
                                title={dims}
                                style={{ width: '100%' }}
                              />
                            );
                          }}
                        </Form.Item>
                      </Form.Item>
                    </Col>
                    {canEditSupplierPurchasePrices && (
                      <>
                        <Col xs={12} md={6}>
                          <Form.Item
                            {...fieldProps}
                            name={[field.name, 'unitCost']}
                            label={t('orders:supplierOrders.form.unitCost')}
                            rules={[{ required: true }, supplierOrderAmountRule(t)]}
                          >
                            <InputNumber
                              precision={2}
                              style={{ width: '100%' }}
                              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Item
                            {...fieldProps}
                            name={[field.name, 'currency']}
                            label={t('orders:supplierOrders.form.purchaseCurrency')}
                            extra={t('orders:supplierOrders.form.purchaseCurrencyHelp')}
                            rules={[{ required: true, message: t('orders:modal.validation.currencyRequired') }]}
                          >
                            <Select style={{ width: '100%' }}>
                              {ORDER_CURRENCIES.map((currency) => (
                                <Select.Option key={currency} value={currency}>
                                  {currency}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </Row>
                  {canEditSupplierPurchasePrices && (
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => {
                        const item = getFieldValue(['items', field.name]) as SupplierOrderItemDraft | undefined;
                        if (!isDefaultPurchaseAutofillApplied(item)) return null;
                        return (
                          <Text type="secondary" style={{ display: 'block', marginTop: -4 }}>
                            {t('orders:supplierOrders.form.defaultPurchaseApplied', {
                              amount: formatDraftMoney(Number(item?.defaultPurchasePrice || 0), item?.defaultPurchaseCurrency || 'PLN'),
                            })}
                          </Text>
                        );
                      }}
                    </Form.Item>
                  )}

                  {watchedPurchaseMode === 'CUSTOMER_ORDER' && (
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => {
                        const item = getFieldValue(['items', field.name]) as SupplierOrderItemDraft | undefined;
                        if (!item) return null;
                        const balance = calculateSupplierItemAllocationBalance(item as any);
                        const number = (value: number | null) => value == null
                          ? '—'
                          : value.toLocaleString('pl-PL', { maximumFractionDigits: 3 });
                        const customerUnit = formatUnit(item.customerUnit);
                        const supplierUnit = formatUnit(item.unit);
                        const hasSurplus = Number(balance.surplusInCustomerUnit || 0) > 0.0001;

                        return (
                          <div style={{
                            marginTop: 8,
                            padding: 16,
                            border: '1px solid #bfdbfe',
                            borderRadius: 10,
                            background: 'linear-gradient(135deg, #f8fbff 0%, #eff6ff 100%)',
                          }}>
                            <Text strong style={{ display: 'block', marginBottom: 12 }}>
                              {t('orders:supplierOrders.allocation.balanceTitle')}
                            </Text>
                            <Row gutter={[12, 12]}>
                              <Col xs={24} md={8}>
                                <Form.Item
                                  {...fieldProps}
                                  name={[field.name, 'customerQuantity']}
                                  label={t('orders:supplierOrders.allocation.customerTarget')}
                                  rules={[{ required: true }, supplierOrderQuantityRule(t)]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <InputNumber
                                    step={0.001}
                                    precision={3}
                                    addonAfter={customerUnit}
                                    style={{ width: '100%' }}
                                    {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={8}>
                                <Text type="secondary">{t('orders:supplierOrders.allocation.purchaseEquivalent')}</Text>
                                <div style={{ fontSize: 17, fontWeight: 650, marginTop: 6 }}>
                                  {number(balance.supplierEquivalentInCustomerUnit)} {customerUnit}
                                </div>
                              </Col>
                              <Col xs={12} md={8}>
                                <Text type="secondary">{t('orders:supplierOrders.allocation.marginQuantity')}</Text>
                                <div style={{ fontSize: 17, fontWeight: 650, marginTop: 6, color: '#1d4ed8' }}>
                                  {number(balance.allocatedSupplierQuantity)} {supplierUnit}
                                </div>
                              </Col>
                            </Row>

                            {balance.error && (
                              <Text type="danger" style={{ display: 'block', marginTop: 12 }}>
                                {t('orders:supplierOrders.allocation.missingDimensions')}
                              </Text>
                            )}

                            {hasSurplus && !balance.error && (
                              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #dbeafe' }}>
                                <Text strong>
                                  {t('orders:supplierOrders.allocation.surplus', {
                                    quantity: number(balance.surplusInCustomerUnit),
                                    unit: customerUnit,
                                  })}
                                </Text>
                                <Form.Item
                                  {...fieldProps}
                                  name={[field.name, 'surplusDisposition']}
                                  style={{ margin: '10px 0 0' }}
                                >
                                  <Radio.Group>
                                    <Space wrap>
                                      <Radio value="STOCK">{t('orders:supplierOrders.allocation.toStock')}</Radio>
                                      <Radio value="CUSTOMER">{t('orders:supplierOrders.allocation.toCustomer')}</Radio>
                                      <Radio value="WASTE">{t('orders:supplierOrders.allocation.toWaste')}</Radio>
                                    </Space>
                                  </Radio.Group>
                                </Form.Item>
                                <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                                  {t(`orders:supplierOrders.allocation.${item.surplusDisposition === 'CUSTOMER'
                                    ? 'toCustomerHelp'
                                    : item.surplusDisposition === 'WASTE'
                                      ? 'toWasteHelp'
                                      : 'toStockHelp'}`)}
                                </Text>
                              </div>
                            )}
                          </div>
                        );
                      }}
                    </Form.Item>
                  )}
                </Card>
              );
            })}
            {watchedPurchaseMode === 'STOCK' && (
              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                disabled={fields.length >= MAX_ITEMS}
                onClick={() => add(buildBlankStockItem())}
              >
                {t('orders:modal.addProduct')}
              </Button>
            )}
            <Form.ErrorList errors={errors} />
          </>
        )}
      </Form.List>

    </Card>
  );

  const renderStep2 = () => (
    <Card title={t('orders:wizard.steps.datesRefs')}>
      <Row gutter={[20, 0]}>
        <Col xs={24} lg={8}>
          <Form.Item
            name="expectedDelivery"
            label={t('orders:supplierOrders.form.expectedDelivery')}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        <Col xs={24} lg={8}>
          <Form.Item
            name="supplierReference"
            label={t('orders:supplierOrders.form.supplierReference')}
          >
            <Input style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        <Col xs={24} lg={8}>
          <Form.Item name="proformaNumber" label={t('orders:supplierOrders.form.proformaNumber')}>
            <Input style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  const renderStep3 = () => {
    const allValues = form.getFieldsValue(true);
    const selectedCustomerOrder = customerOrders.find((order) => order.id === allValues.customerOrderId);
    const selectedSupplier = suppliers.find((s) => s.id === allValues.supplierId);
    const items: SupplierOrderItemDraft[] = (allValues.items || []).filter((item: SupplierOrderItemDraft) =>
      String(item?.name || '').trim()
    );
    const summaryItems: SupplierOrderSummaryItemDraft[] = items.map((item, index) => ({
      ...item,
      summaryKey: `${String(item.name || 'item')}-${index}`,
    }));
    const purchaseTotalsByCurrency = items.reduce<Record<string, number>>((totals, item) => {
      const quantity = Number(item.quantity || 0);
      const unitCost = Number(item.unitCost || 0);
      const lineCurrency = item.currency || allValues.currency || 'PLN';
      if (Number.isFinite(quantity) && Number.isFinite(unitCost)) {
        totals[lineCurrency] = (totals[lineCurrency] || 0) + quantity * unitCost;
      }
      return totals;
    }, {});
    const purchaseTotalEntries = Object.entries(purchaseTotalsByCurrency);

    return (
      <>
        <Card title={t('orders:wizard.steps.summary')} style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label={t('orders:supplierOrders.purchaseMode.label')}>
              {t(`orders:supplierOrders.purchaseMode.${allValues.purchaseMode === 'STOCK' ? 'stock' : 'customerOrder'}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('orders:wizard.summary.supplier')}>
              {selectedSupplier?.name || '-'}
            </Descriptions.Item>
            {allValues.purchaseMode === 'CUSTOMER_ORDER' && (
              <Descriptions.Item label={t('orders:wizard.summary.customerOrder')}>
                {selectedCustomerOrder
                  ? buildCustomerOrderLinkOption(selectedCustomerOrder).label
                  : '-'}
              </Descriptions.Item>
            )}
            {canEditSupplierPurchasePrices && (
              <Descriptions.Item label={t('orders:wizard.summary.purchaseCurrencies')}>
                {purchaseTotalEntries.length > 0
                  ? purchaseTotalEntries.map(([currency]) => currency).join(', ')
                  : '-'}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('orders:wizard.summary.orderDate')}>
              {allValues.orderDate ? dayjs(allValues.orderDate).format('YYYY-MM-DD') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('orders:supplierOrders.form.numberingMode')}>
              {allValues.useManualNumbering
                ? allValues.manualOrderNumber || '-'
                : autoNumberPreview || t('orders:supplierOrders.form.automaticNumbering')}
            </Descriptions.Item>
            <Descriptions.Item label={t('orders:wizard.summary.itemsCount')}>
              {items.length}
            </Descriptions.Item>
            {canEditSupplierPurchasePrices && (
              <Descriptions.Item label={t('orders:wizard.summary.totalPurchaseCost')}>
                {purchaseTotalEntries.length > 0 ? (
                  <Space direction="vertical" size={0}>
                    {purchaseTotalEntries.map(([currency, total]) => (
                      <span key={currency}>{formatDraftMoney(total, currency)}</span>
                    ))}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('orders:wizard.summary.purchasePlnAfterSave')}
                    </Text>
                  </Space>
                ) : '-'}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('orders:wizard.summary.expectedDelivery')}>
              {allValues.expectedDelivery
                ? dayjs(allValues.expectedDelivery).format('YYYY-MM-DD')
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title={t('orders:wizard.review.items')} style={{ marginBottom: 16 }}>
          <Table
            size="small"
            rowKey="summaryKey"
            dataSource={summaryItems}
            pagination={false}
            columns={[
              {
                title: t('orders:productName'),
                dataIndex: 'name',
              },
              {
                title: t('orders:quantity'),
                width: 120,
                render: (_: unknown, item: SupplierOrderSummaryItemDraft) =>
                  formatSupplierOrderQuantity(item, t, i18n.language),
              },
              {
                title: t('orders:modal.dimensionsShort'),
                width: 160,
                render: (_: unknown, item: SupplierOrderSummaryItemDraft) => formatProductDimensions(item) || '-',
              },
              ...(canEditSupplierPurchasePrices
                ? [
                    {
                      title: t('orders:supplierOrders.form.unitCost'),
                      width: 150,
                      render: (_: unknown, item: SupplierOrderSummaryItemDraft) =>
                        formatDraftMoney(Number(item.unitCost || 0), item.currency || allValues.currency || 'PLN'),
                    },
                    {
                      title: t('orders:supplierOrders.form.purchaseCurrency'),
                      width: 130,
                      render: (_: unknown, item: SupplierOrderSummaryItemDraft) =>
                        item.currency || allValues.currency || 'PLN',
                    },
                    {
                      title: t('orders:wizard.summary.purchaseLineValue'),
                      width: 150,
                      render: (_: unknown, item: SupplierOrderSummaryItemDraft) => {
                        const quantity = Number(item.quantity || 0);
                        const unitCost = Number(item.unitCost || 0);
                        const lineTotal =
                          Number.isFinite(quantity) && Number.isFinite(unitCost)
                            ? quantity * unitCost
                            : 0;
                        return formatDraftMoney(lineTotal, item.currency || allValues.currency || 'PLN');
                      },
                    },
                  ]
                : []),
            ]}
          />
        </Card>

        <Card>
          <Form.Item
            name="status"
            label={t('orders:supplierOrders.form.status')}
            rules={[{ required: true }]}
          >
            <Select style={{ width: 200 }} disabled>
              <Select.Option value="DRAFT">
                {t('orders:supplierOrders.status.DRAFT')}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="notes" label={t('orders:notes')}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Card>
      </>
    );
  };

  return (
    <div className="supplier-order-create-shell">
      <header className="supplier-order-create-header">
        <div>
          <Title level={2}>{t('orders:wizard.supplierTitle')}</Title>
          <Text type="secondary">{t('orders:wizard.supplierIntro')}</Text>
        </div>
        <div className="supplier-order-create-drafts">
          <OrderDraftToolbar
            ref={draftToolbarRef}
            type="SUPPLIER"
            buildPayload={buildDraftPayload}
            applyPayload={applyDraftPayload}
            defaultDraftName={draftDefaultName}
          />
        </div>
      </header>

      {renderStepRail()}

      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        className="supplier-order-create-form"
      >
        {/* All steps stay mounted so Ant Design Form preserves field state, validation, and isFieldsTouched across step changes. */}
        <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>{renderStep0()}</div>
        <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>{renderStep1()}</div>
        <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>{renderStep2()}</div>
        <div style={{ display: currentStep === 3 ? 'block' : 'none' }}>{renderStep3()}</div>
      </Form>

      <div className="supplier-order-create-actions">
        <Button onClick={handleCancel}>{t('orders:wizard.cancel')}</Button>
        <Space>
          <Button disabled={currentStep === 0} onClick={handleBack}>
            {t('orders:wizard.back')}
          </Button>
          {currentStep < stepItems.length - 1 ? (
            <Button
              type="primary"
              loading={currentStep === 0 && customerItemsLoading}
              disabled={currentStep === 0 && customerItemsLoading}
              onClick={handleNext}
            >
              {t('orders:wizard.next')}
            </Button>
          ) : (
            <Button type="primary" loading={submitting} onClick={handleSubmit}>
              {t('orders:wizard.submitSupplier')}
            </Button>
          )}
        </Space>
      </div>

      <Modal
        title={t('orders:supplierOrders.form.addSupplier')}
        open={supplierModalVisible}
        onCancel={() => setSupplierModalVisible(false)}
        footer={null}
      >
        <Form
          form={supplierForm}
          layout="vertical"
          onFinish={handleCreateSupplier}
          initialValues={{ defaultCurrency: 'PLN' }}
        >
          <Form.Item
            name="name"
            label={t('suppliers:form.name')}
            rules={[{ required: true, message: t('suppliers:validation.nameRequired') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="contactName" label={t('suppliers:form.contactName')}>
            <Input />
          </Form.Item>
          <Form.Item name="contactPhone" label={t('suppliers:form.contactPhone')}>
            <Input />
          </Form.Item>
          <Form.Item
            name="contactEmail"
            label={t('suppliers:form.contactEmail')}
            rules={[{ type: 'email', message: t('suppliers:validation.invalidEmail') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="taxId" label={t('suppliers:form.taxId')}>
            <Input />
          </Form.Item>
          <Form.Item name="defaultCurrency" label={t('suppliers:form.defaultCurrency')}>
            <Select allowClear placeholder="—">
              {SUPPLIER_CURRENCIES.map((currency) => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="address" label={t('suppliers:form.address')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={supplierSubmitting}>
                {t('common:create')}
              </Button>
              <Button onClick={() => setSupplierModalVisible(false)}>{t('common:cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('orders:supplierOrders.form.addFactory')}
        open={factoryModalVisible}
        onCancel={() => setFactoryModalVisible(false)}
        footer={null}
      >
        <Form form={factoryForm} layout="vertical" onFinish={handleCreateFactory}>
          <Form.Item name="name" label={t('common:name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="taxId" label={t('orders:supplierOrders.form.taxId')}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label={t('orders:supplierOrders.form.address')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={factorySubmitting}>
                {t('common:create')}
              </Button>
              <Button onClick={() => setFactoryModalVisible(false)}>{t('common:cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierOrderCreateWizard;
